// Theme context — handles light / dark / custom themes with persistence.
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "mysavio:theme";
const ACCENT_KEY = "mysavio:custom-accent";
const DEFAULT_ACCENT = "#7E57C2"; // a pleasant violet by default for "custom"
const ALLOWED = ["light", "dark", "custom"];

// ---------- color helpers ----------
function hexToRgb(hex) {
  const m = hex.replace("#", "").match(/^([0-9a-f]{6})$/i);
  if (!m) return { r: 95, g: 113, b: 97 };
  const v = m[1];
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

function darken(hex, amount = 0.18) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const v = (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "light";
    return ALLOWED.includes(v) ? v : "light";
  });
  const [accent, setAccentState] = useState(() => {
    return (typeof window !== "undefined" && localStorage.getItem(ACCENT_KEY)) || DEFAULT_ACCENT;
  });

  // Apply data-theme + accent CSS vars to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    if (theme === "custom") {
      root.style.setProperty("--accent", accent);
      root.style.setProperty("--accent-hover", darken(accent, 0.2));
      root.style.setProperty("--accent-soft", rgba(accent, 0.1));
      root.style.setProperty("--accent-border", rgba(accent, 0.3));
      root.style.setProperty("--accent-strong", rgba(accent, 0.92));
    } else {
      root.style.removeProperty("--accent");
      root.style.removeProperty("--accent-hover");
      root.style.removeProperty("--accent-soft");
      root.style.removeProperty("--accent-border");
      root.style.removeProperty("--accent-strong");
    }
  }, [theme, accent]);

  const setTheme = useCallback((next) => {
    if (!ALLOWED.includes(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  const setAccent = useCallback((hex) => {
    if (typeof hex !== "string" || !/^#[0-9a-f]{6}$/i.test(hex)) return;
    localStorage.setItem(ACCENT_KEY, hex);
    setAccentState(hex);
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, accent, setAccent }),
    [theme, setTheme, accent, setAccent]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
